import { useEffect } from 'react';
import {
  useResponsive,
  useCookieConsent,
  useAuthContext,
  BuyerOrgInitiative,
} from '@emfood/shared';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';

import { usePathname } from 'src/routes/hooks';

import { ENVIRONMENT } from 'src/config-global';

import Logo from 'src/components/logo';
import Scrollbar from 'src/components/scrollbar';
import { NavSectionVertical } from 'src/components/nav-section';

import { useNavData } from './config-navigation';
import { NAV, COOKIE_BANNER } from '../config-layout';
import NavToggleButton from '../common/nav-toggle-button';
import NavFeedbackButton from '../common/nav-feedback-button';

// ----------------------------------------------------------------------

type Props = {
  openNav: boolean;
  onCloseNav: VoidFunction;
};

function NavLogo() {
  const { user } = useAuthContext();
  const { buyerOrg } = user || {};

  // Try to get buyer org from user to show bespoke logo
  if (buyerOrg?.bespokeLogoUrl) {
    return (
      <Stack alignItems="center" spacing={2}>
        <Logo
          sx={{ height: '141px', width: '200px', objectFit: 'contain', pr: 2 }}
          source={buyerOrg.bespokeLogoUrl}
          alt="Buyer organisation logo"
        />

        <Logo
          height="auto"
          width={160}
          source="/logo/ac-logo-horizontal.png"
          alt="Government Supplier Agency and Entegra logos"
        />
      </Stack>
    );
  }

  // Else base on buyer org initiative
  switch (buyerOrg?.initiative) {
    case BuyerOrgInitiative.BBF:
      return (
        <Logo
          height="auto"
          width={200}
          source="/logo/bbf-logo.png"
          alt="Government Supplier Agency and Entegra logos"
        />
      );
    default:
      return (
        <Logo
          height="auto"
          width={175}
          sx={{ marginBottom: '20px' }}
          source="/logo/EqmLogo.png"
          alt="AgileChain logo"
        />
      );
  }
}

export default function NavVertical({ openNav, onCloseNav }: Props) {
  const user = {} as any;

  const { shouldDisplayCookieBanner } = useCookieConsent();

  const smDown = useResponsive('down', 'sm');

  const pathname = usePathname();

  const lgUp = useResponsive('up', 'lg');

  const navData = useNavData();

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'background.paper',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: 3,
          mb: 1,
        }}
      >
        <NavLogo />
      </Box>

      <NavSectionVertical
        data={navData}
        slotProps={{
          currentRole: user?.role,
        }}
      />

      <Box sx={{ flexGrow: 1 }} />

      <NavFeedbackButton />
    </Scrollbar>
  );

  return (
    <Box
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.W_VERTICAL },
      }}
    >
      <NavToggleButton
        sx={{
          backgroundColor: 'background.paper',
          ...(!shouldDisplayCookieBanner
            ? {}
            : {
                mt: smDown ? COOKIE_BANNER.MOBILE : COOKIE_BANNER.DESKTOP,
              }),
        }}
      />

      {lgUp ? (
        <Stack
          sx={{
            height: 1,
            position: 'fixed',
            width: NAV.W_VERTICAL,
            borderRight: (theme) => `dashed 1px ${theme.palette.divider}`,
          }}
        >
          {renderContent}
        </Stack>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          PaperProps={{
            sx: {
              width: NAV.W_VERTICAL,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
